/**
 * Ciclos configurados (instancias por semana) vinculados a una plantilla cíclica.
 * Persistencia local hasta API.
 */

export const STORAGE_CICLOS_CICLICOS = 'pc_ciclos_ciclicos_v2';
const STORAGE_V1 = 'pc_ciclos_ciclicos_v1';

/** Formato corto para listados: "10 mar – 16 mar 2026" */
export function formatearRangoFechas(fechaInicio, fechaFin) {
  try {
    const a = new Date(`${fechaInicio}T12:00:00`);
    const b = new Date(`${fechaFin}T12:00:00`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '—';
    const o = { day: 'numeric', month: 'short' };
    const y = a.getFullYear() === b.getFullYear() ? a.getFullYear() : `${a.getFullYear()} / ${b.getFullYear()}`;
    return `${a.toLocaleDateString('es-ES', o)} – ${b.toLocaleDateString('es-ES', o)} ${b.getFullYear()}`;
  } catch {
    return '—';
  }
}

export function formatearFechaHora(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-ES', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

export function etiquetaSemana(anio, numeroSemana) {
  if (!anio || !numeroSemana) return '—';
  return `Semana ${numeroSemana} · ${anio}`;
}

/** Título estilo planilla: "DEL 16 AL 22 DE MARZO DE 2026" */
export function formatearPeriodoTitulo(fechaInicio, fechaFin) {
  try {
    const a = new Date(`${fechaInicio}T12:00:00`);
    const b = new Date(`${fechaFin}T12:00:00`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '—';
    const mes = a.toLocaleDateString('es-ES', { month: 'long' });
    const anio = b.getFullYear();
    const da = a.getDate();
    const db = b.getDate();
    if (a.getMonth() !== b.getMonth()) {
      const mesB = b.toLocaleDateString('es-ES', { month: 'long' });
      return `DEL ${da} DE ${mes.toUpperCase()} AL ${db} DE ${mesB.toUpperCase()} DE ${anio}`;
    }
    return `DEL ${da} AL ${db} DE ${mes.toUpperCase()} DE ${anio}`;
  } catch {
    return '—';
  }
}

const DIAS_KEYS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

/**
 * Construye grupos de filas (tiempo con rowspan) + filas con rowKey estable.
 * rowKey = `b${bloqueIndex}-r${r}` dentro del bloque.
 */
export function buildFilasGrilla(plantilla) {
  const bloques = Array.isArray(plantilla?.bloques) ? plantilla.bloques : [];
  const grupos = [];
  bloques.forEach((bloque, bloqueIndex) => {
    const orden = Array.isArray(bloque.ordenTipos) ? bloque.ordenTipos : [];
    const filas = orden.map((tipoPlatoId, r) => ({
      rowKey: `b${bloqueIndex}-r${r}`,
      tipoPlatoId,
      bloqueIndex,
    }));
    if (filas.length)
      grupos.push({
        tiempoId: bloque.tiempoId,
        bloqueIndex,
        filas,
      });
  });
  return grupos;
}

/** Matriz: { [diaKey]: { [rowKey]: texto } } */
function crearMatrizPlaceholder(plantilla) {
  const grupos = buildFilasGrilla(plantilla);
  const matriz = {};
  DIAS_KEYS.forEach((k) => {
    matriz[k] = {};
    grupos.forEach((g) => {
      g.filas.forEach((f) => {
        matriz[k][f.rowKey] = '';
      });
    });
  });
  return matriz;
}

/** Matrices de ejemplo para ciclos antiguos con id `ciclo-demo-*` (ya no se crean al iniciar). */
const MATRIZ_PRESETS = {
  'ciclo-demo-001': () => {
    const dias = DIAS_KEYS;
    const filas = [
      'b0-r0',
      'b0-r1',
      'b0-r2',
      'b1-r0',
      'b1-r1',
      'b1-r2',
      'b1-r3',
      'b2-r0',
      'b2-r1',
      'b2-r2',
    ];
    const porFila = {
      'b0-r0': ['Soya c/ piña', 'Jugo de naranja', 'Mate c/ leche', 'Café', 'Chocolate', 'Infusión', 'Café c/ leche'],
      'b0-r1': 'Pan integral + queso|Tostadas + mermelada|Pan + huevo duro|Sandwich mixto|Pan con chicharrón|Medialunas|Pan con mantequilla'.split(
        '|',
      ),
      'b0-r2': ['Manzana', 'Banana', 'Yogurt', 'Gelatina', 'Flan', 'Fruta estación', 'Budín'],
      'b1-r0': 'Sopa de casa|Cream de verduras|Cazuela de pollo|Sopa de lentejas|Chupe de pescado|Sopa de olla|Cazuela'.split(
        '|',
      ),
      'b1-r1': 'Guiso de pallar|Pollo al horno|Pescado frito|Lomo al jugo|Carne al jugo|Arroz con pollo|Asado'.split(
        '|',
      ),
      'b1-r2': 'Zarza criolla|Ensalada mixta|Papa dorada|Arroz con choclo|Puré|Papa dorada + ensalada fresca|Tomate relleno'.split(
        '|',
      ),
      'b1-r3': ['Gaseosa', 'Gaseosa', 'Gaseosa', 'Gaseosa', 'Gaseosa', 'Gaseosa', 'Gaseosa'],
      'b2-r0': ['Mate de coca', 'Infusión', 'Té', 'Agua', 'Jugo', 'Té', 'Agua'],
      'b2-r1': 'Sopa Carolina|Guiso de lentejas|Pollo a la plancha|Hamburguesa|Milanesa|Pizza|Pasta'.split('|'),
      'b2-r2': ['Gelatina', 'Flan', 'Fruta', 'Helado', 'Mousse', 'Compota', 'Piña en almíbar'],
    };
    const matriz = {};
    dias.forEach((d, i) => {
      matriz[d] = {};
      filas.forEach((rk) => {
        const arr = porFila[rk];
        matriz[d][rk] = Array.isArray(arr) ? arr[i] : arr;
      });
    });
    return matriz;
  },
  'ciclo-demo-002': () => {
    const filas = ['b0-r0', 'b0-r1', 'b0-r2', 'b0-r3', 'b1-r0', 'b1-r1', 'b1-r2'];
    const porFila = {
      'b0-r0': ['—', '—', '—', '—', '—', 'Pescado frito', 'Pastel de tallarín + sachet'],
      'b0-r1': ['—', '—', '—', '—', '—', 'Papa dorada + ensalada', 'Zarza criolla'],
      'b0-r2': ['—', '—', '—', '—', '—', 'Gaseosa', 'Gaseosa'],
      'b0-r3': ['—', '—', '—', '—', '—', 'Gelatina', 'Flan'],
      'b1-r0': ['—', '—', '—', '—', '—', 'Infusión', 'Mate'],
      'b1-r1': ['—', '—', '—', '—', '—', 'Pizza', 'Ensalada'],
      'b1-r2': ['—', '—', '—', '—', '—', 'Gelatina', 'Piña en almíbar'],
    };
    const matriz = {};
    DIAS_KEYS.forEach((d, i) => {
      matriz[d] = {};
      filas.forEach((rk) => {
        matriz[d][rk] = porFila[rk][i];
      });
    });
    return matriz;
  },
  'ciclo-demo-003': () => MATRIZ_PRESETS['ciclo-demo-001'](),
};

export function getMatrizResuelta(ciclo, plantilla) {
  if (!plantilla) return { matriz: {}, grupos: [] };
  const grupos = buildFilasGrilla(plantilla);
  if (ciclo?.matrizPlatos && typeof ciclo.matrizPlatos === 'object' && Object.keys(ciclo.matrizPlatos).length > 0) {
    return { matriz: ciclo.matrizPlatos, grupos };
  }
  const preset = ciclo?.id && MATRIZ_PRESETS[ciclo.id];
  if (preset) return { matriz: preset(), grupos };
  return { matriz: crearMatrizPlaceholder(plantilla), grupos };
}

export function normalizeCiclo(raw) {
  const id = raw.id || `ciclo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const nombre = String(raw.nombre || '').trim() || 'Cíclico sin nombre';
  const plantillaId = raw.plantillaId != null ? String(raw.plantillaId) : '';
  const proyectoId = raw.proyectoId != null ? String(raw.proyectoId) : '';
  const anio = parseInt(raw.anio, 10) || new Date().getFullYear();
  const numeroSemana = parseInt(raw.numeroSemana, 10) || 1;
  const fechaInicio = raw.fechaInicio ? String(raw.fechaInicio).slice(0, 10) : '';
  const fechaFin = raw.fechaFin ? String(raw.fechaFin).slice(0, 10) : '';
  let fechaCreacion = raw.fechaCreacion ? String(raw.fechaCreacion) : new Date().toISOString();
  try {
    const t = new Date(fechaCreacion).getTime();
    if (Number.isNaN(t)) fechaCreacion = new Date().toISOString();
  } catch {
    fechaCreacion = new Date().toISOString();
  }
  let matrizPlatos = null;
  if (raw.matrizPlatos && typeof raw.matrizPlatos === 'object') {
    matrizPlatos = raw.matrizPlatos;
  }
  return {
    id,
    nombre,
    plantillaId,
    proyectoId,
    anio,
    numeroSemana,
    fechaInicio,
    fechaFin,
    fechaCreacion,
    matrizPlatos,
  };
}

function isCicloDemoId(id) {
  return String(id || '').startsWith('ciclo-demo-');
}

function stripCiclosDemo(list) {
  return (list || []).filter((c) => !isCicloDemoId(c.id));
}

export function loadCiclosCiclicos() {
  try {
    const raw = localStorage.getItem(STORAGE_CICLOS_CICLICOS);
    if (!raw) {
      const legacy = localStorage.getItem(STORAGE_V1);
      if (legacy) {
        try {
          const parsed = JSON.parse(legacy);
          if (Array.isArray(parsed)) {
            const migrated = stripCiclosDemo(parsed.map(normalizeCiclo));
            localStorage.setItem(STORAGE_CICLOS_CICLICOS, JSON.stringify(migrated));
            return migrated;
          }
        } catch {
          /* ignore */
        }
      }
      return [];
    }
    const p = JSON.parse(raw);
    const list = Array.isArray(p) ? p.map(normalizeCiclo) : [];
    const cleaned = stripCiclosDemo(list);
    if (cleaned.length !== list.length) {
      try {
        localStorage.setItem(STORAGE_CICLOS_CICLICOS, JSON.stringify(cleaned));
      } catch {
        /* ignore */
      }
    }
    return cleaned;
  } catch {
    return [];
  }
}

export function saveCiclosCiclicos(list) {
  localStorage.setItem(STORAGE_CICLOS_CICLICOS, JSON.stringify(list));
}

export function getCicloById(id, list) {
  return (list || []).find((c) => c.id === id) || null;
}

/** Cíclicos que usan una plantilla (misma `plantillaId`). Persistencia local. */
export function ciclosQueUsanPlantilla(plantillaId) {
  if (plantillaId == null || plantillaId === '') return [];
  const pid = String(plantillaId);
  return loadCiclosCiclicos().filter((c) => String(c.plantillaId) === pid);
}
